/* ==========================================================================
   TheHopeTarot — global.js
   Site-wide behaviour shared by every section:
     • Scroll-reveal animations (IntersectionObserver, respects reduced-motion)
     • Sticky header shrink-on-scroll
     • Mobile navigation drawer + hover/tap dropdowns
   Individual sections (slider, accordion, modal, marquee) ship their own small
   scripts. Everything re-initialises on Shopify's `shopify:section:load` event
   so the Theme Customizer preview stays live.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Scroll reveal --------------------------------------------------- */
  function initReveal(root) {
    var scope = root || document;
    var items = Array.prototype.slice.call(scope.querySelectorAll('.reveal:not(.is-visible)'));
    if (!items.length) return;
    var show = function (el) { el.classList.add('is-visible'); };

    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach(show);
      return;
    }
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { show(entry.target); obs.unobserve(entry.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    var vh = window.innerHeight || document.documentElement.clientHeight;
    items.forEach(function (el) {
      // Anything already in (or above) the viewport reveals now — never wait on an
      // async observer for content the visitor can already see.
      var top = el.getBoundingClientRect().top;
      if (top < vh * 0.98) { show(el); return; }
      io.observe(el);
    });

    // Failsafe: some mobile browsers / restored background tabs never fire the
    // observer. Guarantee content is never left permanently invisible.
    setTimeout(function () { items.forEach(show); }, 2600);
  }

  /* ---- Sticky header: shrink on scroll + auto-hide (hide on scroll down,
     reveal on scroll up) so the menu is reachable without going to the top. --- */
  function initHeader() {
    var header = document.querySelector('[data-sticky-header]');
    if (!header || header._hopeHeaderCleanup) return;
    var wrap = header.closest('.shopify-section') || header;
    var lastY = window.pageYOffset;
    var ticking = false;
    var apply = function () {
      var y = window.pageYOffset;
      header.classList.toggle('is-scrolled', y > 40);
      var menuActive = document.body.classList.contains('nav-open') || header.querySelector('.is-menu-open') || header.contains(document.activeElement);
      if (header.dataset.stickyEnabled === 'false' || menuActive || y <= 120) {
        wrap.classList.remove('is-hidden');
      } else if (y > lastY + 5) {
        wrap.classList.add('is-hidden');
      } else if (y < lastY - 5) {
        wrap.classList.remove('is-hidden');
      }
      lastY = y;
      ticking = false;
    };
    var onScroll = function () {
      if (!ticking) { window.requestAnimationFrame(apply); ticking = true; }
    };
    apply();
    window.addEventListener('scroll', onScroll, { passive: true });

    var menuItems = Array.prototype.slice.call(header.querySelectorAll('.hth-menu__item.has-children'));
    var cleanups = [];
    function listen(target, event, handler) {
      target.addEventListener(event, handler);
      cleanups.push(function () { target.removeEventListener(event, handler); });
    }
    function setMenu(item, open) {
      item.classList.toggle('is-menu-open', open);
      var button = item.querySelector('[data-mega-toggle]');
      if (button) button.setAttribute('aria-expanded', String(open));
      if (open) wrap.classList.remove('is-hidden');
    }
    function closeOthers(item) { menuItems.forEach(function (other) { if (other !== item) setMenu(other, false); }); }
    menuItems.forEach(function (item) {
      var button = item.querySelector('[data-mega-toggle]');
      listen(item, 'mouseenter', function () { closeOthers(item); setMenu(item, true); });
      listen(item, 'mouseleave', function () { if (!item.contains(document.activeElement)) setMenu(item, false); });
      listen(item, 'focusin', function () { closeOthers(item); });
      listen(item, 'focusout', function (event) { if (!item.contains(event.relatedTarget)) setMenu(item, false); });
      if (button) listen(button, 'click', function () {
        var open = button.getAttribute('aria-expanded') !== 'true';
        closeOthers(item); setMenu(item, open);
      });
      listen(item, 'keydown', function (event) {
        if (event.key === 'Escape') {
          event.preventDefault(); setMenu(item, false);
          if (button) button.focus();
        } else if (event.key === 'ArrowDown' && (event.target === button || event.target.classList.contains('hth-menu__link'))) {
          event.preventDefault(); closeOthers(item); setMenu(item, true);
          var firstLink = item.querySelector('.hth-mega a');
          if (firstLink) firstLink.focus();
        }
      });
    });
    listen(document, 'pointerdown', function (event) { if (!header.contains(event.target)) closeOthers(null); });
    header._hopeHeaderCleanup = function () {
      window.removeEventListener('scroll', onScroll);
      cleanups.forEach(function (cleanup) { cleanup(); });
      delete header._hopeHeaderCleanup;
    };
  }

  /* ---- Mobile nav drawer ---------------------------------------------- */
  function initMobileNav() {
    document.querySelectorAll('[data-nav-toggle]').forEach(function (toggle) {
      if (toggle._hopeNavCleanup) return;
      var drawer = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!drawer) return;
      var panel = drawer.querySelector('[role="dialog"]');
      var wrap = toggle.closest('.shopify-section');
      var open = false;
      var cleanups = [];
      function listen(target, event, handler) {
        target.addEventListener(event, handler);
        cleanups.push(function () { target.removeEventListener(event, handler); });
      }
      function setState(next, returnFocus) {
        open = next;
        toggle.setAttribute('aria-expanded', String(open));
        toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        drawer.hidden = !open;
        drawer.setAttribute('aria-hidden', String(!open));
        drawer.classList.toggle('is-open', open);
        document.body.classList.toggle('nav-open', open);
        if (open) {
          if (wrap) wrap.classList.remove('is-hidden');
          var close = drawer.querySelector('button[data-nav-close]');
          (close || panel).focus({ preventScroll: true });
        } else if (returnFocus !== false && toggle.isConnected) {
          toggle.focus({ preventScroll: true });
        }
      }
      listen(toggle, 'click', function () { setState(!open); });
      listen(drawer, 'click', function (event) {
        if (event.target.closest('[data-nav-close]')) setState(false);
        else if (event.target.closest('a')) setState(false, false);
      });
      listen(document, 'keydown', function (event) {
        if (!open) return;
        if (event.key === 'Escape') { event.preventDefault(); setState(false); return; }
        if (event.key !== 'Tab') return;
        var focusable = Array.prototype.slice.call(panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]')).filter(function (element) {
          return !element.closest('[hidden]') && element.getClientRects().length > 0;
        });
        if (!focusable.length) { event.preventDefault(); panel.focus(); return; }
        var first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
          event.preventDefault(); first.focus();
        }
      });
      drawer.querySelectorAll('[data-submenu-toggle]').forEach(function (button) {
        var submenu = document.getElementById(button.getAttribute('aria-controls'));
        if (!submenu) return;
        listen(button, 'click', function () {
          var expanded = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!expanded));
          submenu.hidden = expanded;
        });
      });
      var onResize = function () { if (open && window.innerWidth >= 1000) setState(false, false); };
      listen(window, 'resize', onResize);
      toggle._hopeNavCleanup = function () {
        if (open) setState(false, false);
        cleanups.forEach(function (cleanup) { cleanup(); });
        delete toggle._hopeNavCleanup;
      };
    });
  }

  /* ---- Predictive search ---------------------------------------------- */
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function runSuggest(q, box) {
    if (!window.fetch) return;
    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product,page,article&resources[limit]=6' +
      '&resources[options][unavailable_products]=last';
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var res = data && data.resources && data.resources.results;
        if (!res) { box.hidden = true; return; }
        var products = res.products || [];
        var allow = window.HTH_SEARCH_REPORTS || [];
        if (allow.length) {
          products = products.filter(function (p) {
            var h = p.handle || (p.url || '').split('/').pop().split('?')[0];
            return allow.indexOf(h) !== -1;
          });
        }
        var pages = (res.pages || []).filter(function (p) { return (p.handle || p.url || '').indexOf('checkout-') === -1; });
        var articles = res.articles || [];
        var html = '';
        if (products.length) {
          html += '<div class="hth-suggest__group"><span class="hth-suggest__label">Reports</span>';
          products.forEach(function (p) {
            var img = p.image || (p.featured_image && p.featured_image.url) || '';
            html += '<a class="hth-suggest__row" href="' + esc(p.url) + '">' +
              (img ? '<img class="hth-suggest__thumb" src="' + esc(img) + '" alt="" loading="lazy">' : '<span class="hth-suggest__thumb hth-suggest__thumb--ph"></span>') +
              '<span class="hth-suggest__ttl">' + esc(p.title) + '</span></a>';
          });
          html += '</div>';
        }
        var others = pages.concat(articles);
        if (others.length) {
          var calcMap = window.HTH_CALC_IMAGES || {};
          html += '<div class="hth-suggest__group"><span class="hth-suggest__label">Calculators &amp; guides</span>';
          others.forEach(function (p) {
            var handle = p.handle || (p.url || '').split('/').pop().split('?')[0];
            var cimg = calcMap[handle];
            html += '<a class="hth-suggest__row" href="' + esc(p.url) + '">' +
              (cimg ? '<img class="hth-suggest__thumb" src="' + esc(cimg) + '?width=120" alt="" loading="lazy">' : '<span class="hth-suggest__thumb hth-suggest__thumb--ph"></span>') +
              '<span class="hth-suggest__ttl">' + esc(p.title) + '</span></a>';
          });
          html += '</div>';
        }
        if (!html) {
          html = '<p class="hth-suggest__none">No matches for “' + esc(q) + '”.</p>';
        }
        html += '<a class="hth-suggest__all" href="/search?q=' + encodeURIComponent(q) + '&type=product,page,article">See all results for “' + esc(q) + '”</a>';
        box.innerHTML = html;
        box.hidden = false;
      })
      .catch(function () { box.hidden = true; });
  }

  /* ---- Slide-down header search --------------------------------------- */
  function initSearchToggle(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-search-toggle]').forEach(function (toggle) {
      if (toggle.dataset.bound) return;
      toggle.dataset.bound = '1';
      var panelId = toggle.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) return; // no JS panel → the icon stays a plain link to /search
      var input = panel.querySelector('[data-search-input]');
      var closeBtn = panel.querySelector('[data-search-close]');
      var setOpen = function (open) {
        panel.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
        if (open && input) { input.focus(); }
      };
      toggle.addEventListener('click', function (e) { e.preventDefault(); setOpen(panel.hidden); });
      if (closeBtn) closeBtn.addEventListener('click', function () { setOpen(false); toggle.focus(); });

      /* Predictive (live) results */
      var suggest = panel.querySelector('[data-search-suggest]');
      if (input && suggest) {
        var timer;
        input.addEventListener('input', function () {
          clearTimeout(timer);
          var q = input.value.trim();
          if (q.length < 2) { suggest.hidden = true; suggest.innerHTML = ''; return; }
          timer = setTimeout(function () { runSuggest(q, suggest); }, 220);
        });
      }
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !panel.hidden) { setOpen(false); toggle.focus(); } });
      document.addEventListener('click', function (e) {
        if (panel.hidden) return;
        if (panel.contains(e.target) || toggle.contains(e.target)) return;
        setOpen(false);
      });
    });
  }

  /* ---- Cart count ------------------------------------------------------ */
  function setCartCount(n) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = n;
      if (el.classList.contains('hth-header__cart-count')) el.classList.toggle('is-visible', n > 0);
      else el.hidden = n <= 0;
    });
    var t = document.querySelector('[data-cart-count-text]');
    if (t) t.textContent = n + (n === 1 ? ' item' : ' items') + ' in cart';
  }
  function refreshCart() {
    if (!window.fetch) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { if (c && typeof c.item_count === 'number') setCartCount(c.item_count); })
      .catch(function () {});
  }

  /* ---- Boot ------------------------------------------------------------ */
  function boot(scope) {
    initReveal(scope);
    initHeader();
    initMobileNav();
    initSearchToggle(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  /* Re-run for sections re-rendered by the Theme Customizer */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-sticky-header]').forEach(function (header) { if (header._hopeHeaderCleanup) header._hopeHeaderCleanup(); });
    event.target.querySelectorAll('[data-nav-toggle]').forEach(function (toggle) { if (toggle._hopeNavCleanup) toggle._hopeNavCleanup(); });
  });

  /* Cart count: correct any cached/stale server number, and refresh when the
     page is restored from bfcache or a script signals a cart change. */
  refreshCart();
  window.addEventListener('pageshow', function (e) { if (e.persisted) refreshCart(); });
  document.addEventListener('cart:updated', refreshCart);

  /* Expose so section scripts can trigger reveal on freshly injected nodes,
     and refresh the cart badge after an AJAX add. */
  window.HopeTarot = window.HopeTarot || {};
  window.HopeTarot.initReveal = initReveal;
  window.HopeTarot.refreshCart = refreshCart;
})();
